import { ChartOptions } from "chart.js";

import { displayDataLabel, displayPercent } from "./displayDataLabel";
import { calculate } from "./calculate";

import theme from "../../components/styles/theme/theme";

type BarChartOptions = ChartOptions<"bar">;
export type Title = "economic_value_added" | "employment" | "impact_over_time";

export const verticalChartOptions = (
  title: Title,
  showPercentageValue?: boolean
): BarChartOptions => {
  return {
    plugins: {
      title: {
        text: title,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: "x",
        backgroundColor: theme.palette.common.white,
        borderColor: theme.palette.common.black,
        borderWidth: 1,
        bodyColor: theme.palette.common.black,
        titleColor: theme.palette.common.black,
        filter: (tooltipItem) => (tooltipItem.raw as number) > 0,
        itemSort: (a, b) => {
          const datasetIndexA = a.datasetIndex!;
          const datasetIndexB = b.datasetIndex!;

          return datasetIndexB - datasetIndexA;
        },
      },
      datalabels: {
        display: true,
        color: ({ dataset }) => {
          let backgroundColor: string;
          if (typeof dataset.backgroundColor === "string") {
            backgroundColor = dataset.backgroundColor;
          } else {
            backgroundColor = "defaultColor";
          }
          return theme.palette.getContrastText(backgroundColor);
        },
        labels: {
          value: {
            anchor: "center",
            align: "center",
            formatter: displayDataLabel,
          },
          percentageValue: {
            display: !!showPercentageValue,
            color: theme.palette.common.black,
            anchor: "center",
            align: "right",
            offset: 50,
            formatter: displayPercent,
          },
        },
      },
    },
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    aspectRatio: 1.4,
    layout: {
      padding: {
        top: 20,
        bottom: 20,
      },
      autoPadding: true,
    },
    scales: {
      x: {
        stacked: true,
        grace: 15,
        beginAtZero: false,
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
        stacked: true,
      },
    },
    hover: {
      mode: "x",
    },
    animation: {
      onProgress: ({ chart }) => calculate(chart, title),
      onComplete: ({ chart }) => calculate(chart, title),
    },
  };
};

export const horizontalChartOptions = (title: Title): BarChartOptions => {
  return {
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: "y",
        xAlign: "left",
        backgroundColor: theme.palette.common.white,
        borderColor: theme.palette.common.black,
        borderWidth: 1,
        bodyColor: theme.palette.common.black,
        titleColor: theme.palette.common.black,
        filter: (tooltipItem) => (tooltipItem.raw as number) > 0,
        itemSort: (a, b) => {
          const datasetIndexA = a.datasetIndex!;
          const datasetIndexB = b.datasetIndex!;

          return datasetIndexB - datasetIndexA;
        },
      },
      datalabels: {
        display: false,
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    responsive: true,
    aspectRatio: 1.4,
    layout: {
      padding: {
        right: 50,
      },
      autoPadding: true,
    },
    scales: {
      x: {
        display: false,
        stacked: true,
      },
      y: {
        stacked: true,
        grace: 15,
        beginAtZero: false,
        grid: {
          display: false,
        },
      },
    },
    hover: {
      mode: "y",
    },
    animation: {
      onProgress: ({ chart }) => calculate(chart, title),
      onComplete: ({ chart }) => calculate(chart, title),
    },
  };
};
